import { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { TrendingUp, Users, Package, Calendar, Loader2, BadgeCheck } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-emerald-700" /></div>
      <div className="text-2xl font-extrabold text-slate-900 font-mono">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/partners").then((r) => setPartners(r.data));
    api.get("/admin/bookings").then((r) => setBookings(r.data));
  };
  useEffect(load, []);

  const toggleVerify = async (id) => { await api.patch(`/admin/partners/${id}/verify`); load(); };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-1">Admin-Dashboard</h1>
        <p className="text-slate-500 mb-8">Marktplatz-Übersicht & Partnerverwaltung</p>

        {!stats ? <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto" /> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
              <Stat icon={TrendingUp} label="Gesamtumsatz (GMV)" value={euro(stats.gmv)} />
              <Stat icon={Calendar} label="Buchungen" value={stats.total_bookings} />
              <Stat icon={Users} label="Partner" value={stats.total_partners} />
              <Stat icon={Package} label="Angebote" value={stats.total_services} />
            </div>

            <Tabs defaultValue="partners">
              <TabsList><TabsTrigger value="partners" data-testid="admin-tab-partners">Partner</TabsTrigger><TabsTrigger value="bookings" data-testid="admin-tab-bookings">Buchungen</TabsTrigger></TabsList>

              <TabsContent value="partners" className="mt-6 space-y-3" data-testid="admin-partners">
                {partners.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold">{p.name[0]}</span>
                      <div>
                        <div className="font-semibold text-slate-900 flex items-center gap-1">{p.name} {p.verified && <BadgeCheck className="w-4 h-4 text-emerald-600" />}</div>
                        <div className="text-xs text-slate-500">{p.email} · {p.services} Angebote</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Verifiziert</span>
                      <Switch checked={p.verified} onCheckedChange={() => toggleVerify(p.id)} data-testid={`verify-${p.id}`} />
                    </div>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="bookings" className="mt-6 space-y-3" data-testid="admin-bookings">
                {bookings.map((b) => (
                  <div key={b.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-mono text-slate-400">{b.reference}</div>
                      <div className="font-semibold text-slate-900">{b.service_title}</div>
                      <div className="text-xs text-slate-500">{b.customer_name} → {b.partner_name} · {b.event_date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-emerald-700">{euro(b.total_amount)}</div>
                      <div className="text-xs text-slate-500">{b.payment_status === "paid" ? "Bezahlt" : "Offen"}</div>
                    </div>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
